import React from 'react';

export function CampaignSetupStep({ formData, onChange, errors }) {
  const verticalCategories = {
    'Self-Service Operations': [
      { value: 'parking', label: 'Parking (Off-Street)', description: 'Airports, shopping centers, entertainment venues' },
      { value: 'fuel', label: 'Fuel & Convenience', description: 'Gas stations, truck stops, convenience stores' },
      { value: 'vending', label: 'Vending', description: 'Snack/beverage, specialty vending, micro markets' },
      { value: 'carwash', label: 'Car Wash', description: 'Automated, touchless, self-serve bays' },
      { value: 'laundry', label: 'Laundry', description: 'Laundromats, apartment complexes' },
      { value: 'evcharging', label: 'EV Charging', description: 'Workplace, public, residential charging' },
      { value: 'transit', label: 'Mass Transit/Ticketing', description: 'Parking meters, transit passes, event tickets' },
      { value: 'amusement', label: 'Amusement/Recreation', description: 'Arcade games, ticket redemption' }
    ],
    'Food & Beverage': [
      { value: 'qsr', label: 'Quick Service Restaurants', description: 'QSR, fast-casual, food trucks' },
      { value: 'specialty-food', label: 'Specialty Food & Beverage', description: 'Gourmet, wine, organic foods' }
    ],
    'Retail & Commerce': [
      { value: 'fashion-apparel', label: 'Fashion & Apparel', description: 'Boutiques, pop-ups, specialty chains' },
      { value: 'beauty-cosmetics', label: 'Beauty & Cosmetics', description: 'Salons, spas, specialty retailers' },
      { value: 'electronics-tech', label: 'Electronics & Technology', description: 'Repair shops, specialty electronics' },
      { value: 'home-garden', label: 'Home & Garden', description: 'Furniture, décor, outdoor equipment' },
      { value: 'pet-services', label: 'Pet Services', description: 'Grooming, boutiques, veterinary' },
      { value: 'sporting-goods', label: 'Sporting Goods', description: 'Equipment specialists, outdoor gear' },
      { value: 'health-wellness', label: 'Health & Wellness', description: 'Supplements, fitness, medical devices' },
      { value: 'jewelry-accessories', label: 'Jewelry & Accessories', description: 'Fine jewelry, watches, fashion accessories' },
      { value: 'books-stationery', label: 'Books & Stationery', description: 'Independent stores, educational supplies' },
      { value: 'hobby-crafts', label: 'Hobby & Crafts', description: 'Art supplies, maker spaces, DIY' }
    ]
  };

  const personas = [
    { value: 'business-buyer', label: 'Business Buyers', description: 'C-Suite executives, Operations Managers, Franchise Owners' },
    { value: 'technical-buyer', label: 'Technical Buyers', description: 'IT Directors, Systems Integration Specialists, Payment Processing Managers' },
    { value: 'channel-partner', label: 'Channel Partners', description: 'Payment Resellers, Systems Integrators, Equipment Manufacturers' }
  ];

  const geographicOptions = [
    { value: 'usa', label: 'North America' },
    { value: 'eu', label: 'Europe' },
    { value: 'apac', label: 'APAC' },
    { value: 'br', label: 'Brazil' },
    { value: 'global', label: 'Global' }
  ];

  // Define countries for each region
  const regionCountries = {
    usa: [
      { id: 'United States', name: 'United States' },
    ],
    eu: [
        { id: 'United Kingdom', name: 'United Kingdom' },
        { id: 'Ireland', name: 'Ireland' },
        { id: 'France', name: 'France' },
        { id: 'Spain', name: 'Spain' },
        { id: 'Italy', name: 'Italy' },
        { id: 'Germany', name: 'Germany' }
    ],
    apac: [
      { id: 'Australia', name: 'Australia' },
      { id: 'New Zealand', name: 'New Zealand' },
      { id: 'Malaysia', name: 'Malaysia' },
    ],
    br: [
      { id: 'Brazil', name: 'Brazil' }
    ]
  };

  // Get countries for selected region
  const availableCountries = formData.geographicFocus ? regionCountries[formData.geographicFocus] || [] : [];

  // Reset country when region changes
  const handleRegionChange = (region) => {
    onChange('geographicFocus', region);
    // Clear country selection when region changes
    if (formData.country) {
      onChange('country', '');
    }
  };

  const objectives = [
    { value: 'lead-generation', label: 'Lead Generation', description: 'Drive qualified prospects and sales inquiries' },
    { value: 'education', label: 'Education', description: 'Inform market about TNS capabilities and benefits' },
    { value: 'partner-enablement', label: 'Partner Enablement', description: 'Support channel partners with sales materials' },
    { value: 'thought-leadership', label: 'Thought Leadership', description: 'Position TNS as industry expert and innovator' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Campaign Setup</h2>
        <p className="text-gray-600 mb-8">Define your target market and campaign objectives to create focused, effective marketing content.</p>
      </div>

      {/* Business Vertical Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Business Vertical *
        </label>
        {errors.vertical && (
          <p className="text-red-600 text-sm mb-2">{errors.vertical}</p>
        )}
        <div className="space-y-6">
          {Object.entries(verticalCategories).map(([categoryName, verticals]) => (
            <div key={categoryName}>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                {categoryName}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {verticals.map((vertical) => (
                  <div
                    key={vertical.value}
                    className={`relative rounded-lg border p-3 cursor-pointer transition-colors ${
                      formData.vertical === vertical.value
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => onChange('vertical', vertical.value)}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="vertical"
                        value={vertical.value}
                        checked={formData.vertical === vertical.value}
                        onChange={() => onChange('vertical', vertical.value)}
                        className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                      />
                      <div className="ml-3">
                        <label className="block text-sm font-medium text-gray-900 leading-tight">
                          {vertical.label}
                        </label>
                        <p className="text-xs text-gray-500 mt-1">{vertical.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Audience Persona Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Target Audience Persona *
        </label>
        {errors.audiencePersona && (
          <p className="text-red-600 text-sm mb-2">{errors.audiencePersona}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {personas.map((persona) => (
            <div
              key={persona.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.audiencePersona === persona.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('audiencePersona', persona.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="audiencePersona"
                  value={persona.value}
                  checked={formData.audiencePersona === persona.value}
                  onChange={() => onChange('audiencePersona', persona.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {persona.label}
                  </label>
                  <p className="text-sm text-gray-500">{persona.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Geographic Focus */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Geographic Region
          </label>
          <select
            value={formData.geographicFocus}
            onChange={(e) => handleRegionChange(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
          >
            <option value="">Select region</option>
            {geographicOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Country (Optional)
          </label>
          <select
            value={formData.country}
            onChange={(e) => onChange('country', e.target.value)}
            disabled={!formData.geographicFocus || formData.geographicFocus === 'global'}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Select country</option>
            {availableCountries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
          {!formData.geographicFocus && (
            <p className="text-xs text-gray-500 mt-1">Select a region first</p>
          )}
          {formData.geographicFocus === 'global' && (
            <p className="text-xs text-gray-500 mt-1">Country selection not available for global campaigns</p>
          )}
        </div>
      </div>

      {/* Campaign Objective */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Campaign Objective
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {objectives.map((objective) => (
            <div
              key={objective.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.campaignObjective === objective.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('campaignObjective', objective.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="campaignObjective"
                  value={objective.value}
                  checked={formData.campaignObjective === objective.value}
                  onChange={() => onChange('campaignObjective', objective.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {objective.label}
                  </label>
                  <p className="text-sm text-gray-500">{objective.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}